import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import { menuPanelClass, dataTableFocusRing } from 'features/playground/styles';

/**
 * BrandSelect: the playground's single-value picker.
 *
 *   options: [{ value, label, icon?: ReactNode }]
 *
 * `variant`:
 *   'default': a bordered h-8 field, the shared BrandDropdown (portaled menu,
 *              search once there are 8+ options)
 *   'bare':    label + chevron only, no border or fill. For places where the
 *              select IS the heading (a message's role).
 */
export default function BrandSelect({
  value,
  options,
  onChange,
  placeholder = 'Select',
  disabled = false,
  minWidth = 160,
  leadingIcon = null,
  variant = 'default',
  ariaLabel,
}) {
  if (variant === 'bare') {
    return (
      <BareSelect
        value={value}
        options={options}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        ariaLabel={ariaLabel}
      />
    );
  }
  return (
    <div style={{ minWidth }}>
      <BrandDropdown
        value={value == null ? '' : String(value)}
        options={options.map((o) => ({ ...o, value: String(o.value) }))}
        onChange={(v) => {
          const hit = options.find((o) => String(o.value) === String(v));
          onChange(hit ? hit.value : v);
        }}
        placeholder={placeholder}
        disabled={disabled}
        ariaLabel={ariaLabel}
        triggerClassName={`!h-8 !rounded-md !px-2.5 !text-[12px] hover:!border-[color:var(--border-strong)] ${dataTableFocusRing}`}
        menuClassName="!rounded-lg"
        renderTrigger={(sel) => (
          <span className="flex min-w-0 items-center gap-2">
            {!sel?.icon && leadingIcon}
            <span className="truncate">{sel ? (sel.label || sel.value) : placeholder}</span>
          </span>
        )}
      />
    </div>
  );
}

function BareSelect({ value, options, onChange, placeholder, disabled, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = useMemo(() => options.find((o) => String(o.value) === String(value)), [options, value]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    // Escape closes the list, not the dialog the list sits in.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`-ml-1 flex items-center gap-1 rounded-md px-1 py-0.5 text-left text-[12px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50 ${dataTableFocusRing}`}
      >
        {selected?.icon}
        <span className="whitespace-nowrap">
          {selected?.label || <span className="text-[color:var(--text-disabled)]">{placeholder}</span>}
        </span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[color:var(--text-muted)]" />
      </button>
      {open && (
        <div
          role="listbox"
          className={`absolute left-0 z-50 mt-1 max-h-72 min-w-[150px] overflow-y-auto ${menuPanelClass}`}
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {options.map((opt) => {
            const isSel = String(opt.value) === String(value);
            return (
              <button
                key={String(opt.value)}
                type="button"
                role="option"
                aria-selected={isSel}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`flex h-8 w-full items-center gap-2 px-3 text-left text-[12px] text-[color:var(--text-primary)] outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover ${isSel ? 'bg-surface-hover' : ''}`}
              >
                {opt.icon}
                <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                {isSel && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
